import React from 'react';
import { Plus, Copy, Trash2, Bot, Download, Save, Search, RotateCcw, WrapText, Settings2, AlignLeft, Maximize2, Minimize2 } from 'lucide-react';
import { StepProgressIndicator, NextStepButton } from '../../components/WorkflowNavigation';
import { FmeaStandardConfig as StandardConfig } from '../../constants/fmeaStandards';

interface EditorToolbarProps {
    activeProject: string;
    productName?: string;
    productDescription?: string;
    standardConfig: StandardConfig;
    searchTerm: string;
    setSearchTerm: (val: string) => void;
    viewMode: 'active' | 'draft';
    setViewMode: (mode: 'active' | 'draft') => void;
    activeCount: number;
    draftCount: number;
    addRow: () => void;
    duplicateLastRow: () => void;
    deleteSelectedRows: () => void;
    selectedCount: number;
    handleAiGenerate: () => void;
    resetFilters: () => void;
    wrapText: boolean;
    setWrapText: (val: boolean) => void;
    handleSave: () => void;
    exportProject: (type: 'csv' | 'json') => void;
    onOpenLayoutSettings: () => void;
    rowCount: number;
    isFullScreen: boolean;
    setIsFullScreen: (val: boolean) => void;
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
    activeProject,
    productName,
    productDescription,
    standardConfig,
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    activeCount,
    draftCount,
    addRow,
    duplicateLastRow,
    deleteSelectedRows,
    selectedCount,
    handleAiGenerate,
    resetFilters,
    wrapText,
    setWrapText,
    handleSave,
    exportProject,
    onOpenLayoutSettings,
    rowCount,
    isFullScreen,
    setIsFullScreen,
}) => {
    return (
        <div className="flex flex-col gap-3 mb-2 shrink-0">
            {/* Top Row: Title + Controls */}
            <div className="flex justify-between items-start gap-4">
                <div className="flex flex-col gap-1 min-w-0">
                    <h1 className="text-xl font-bold flex items-center gap-2 text-text-primary">
                        FMEA Editor
                        {activeProject !== 'all' && (
                            <span className="text-sm font-normal text-text-secondary bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded border border-border truncate max-w-[200px]">
                                {productName || activeProject}
                            </span>
                        )}
                    </h1>
                    {activeProject !== 'all' && productDescription && (
                        <p className="text-xs text-text-secondary max-w-xl bg-slate-50 dark:bg-slate-900/50 px-2 py-1 rounded border border-border/50 italic truncate">
                            {productDescription}
                        </p>
                    )}
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-600/10 to-cyan-500/10 border border-blue-500/30 rounded-lg">
                            <span className="text-[10px] text-text-secondary uppercase tracking-wide">Standard:</span>
                            <span className="text-xs font-bold text-blue-400">{standardConfig.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded-full">
                                {standardConfig.riskModel === 'AP' ? 'Action Priority' : 'RPN'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Step Progress + Next Step — top-right inline */}
                <div className="flex items-center gap-3">
                    <StepProgressIndicator currentStep={5} completedSteps={[1, 2, 3, 4]} />
                    <NextStepButton currentStep={5} isComplete={rowCount > 0} className="text-xs px-3 py-1.5" />
                </div>

                <div className="flex items-center gap-2 shrink-0">

                    {/* View Mode Toggle */}
                    <div className="flex bg-background border border-border rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('active')}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-2 ${viewMode === 'active' ? 'bg-emerald-500 text-white shadow-md' : 'text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                        >
                            Active
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${viewMode === 'active' ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700'}`}>{activeCount}</span>
                        </button>
                        <button
                            onClick={() => setViewMode('draft')}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-2 ${viewMode === 'draft' ? 'bg-amber-500 text-white shadow-md' : 'text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                        >
                            Drafts
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${viewMode === 'draft' ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700'}`}>{draftCount}</span>
                        </button>
                    </div>

                    <button
                        onClick={handleSave}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-lg hover:shadow-emerald-500/20 transition-all text-sm font-bold"
                    >
                        <Save size={16} /> Save to DB
                    </button>
                    <button
                        onClick={() => exportProject('csv')}
                        className="flex items-center gap-2 px-4 py-2 bg-background border border-border hover:bg-slate-50 dark:hover:bg-slate-800 text-text-primary rounded-lg transition-all text-sm font-medium"
                    >
                        <Download size={16} /> Export CSV
                    </button>
                    <button
                        onClick={() => setIsFullScreen(!isFullScreen)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm font-medium border ${isFullScreen ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-background hover:bg-slate-50 text-text-primary border-border'}`}
                        title={isFullScreen ? "Exit Focus Mode" : "Enter Focus Mode"}
                    >
                        {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                        {isFullScreen ? 'Exit Focus' : 'Focus Mode'}
                    </button>
                </div>
            </div>

            {/* Action Toolbar */}
            <div className="flex flex-wrap items-center gap-2 p-2.5 bg-card border border-border rounded-xl shadow-sm">
                {/* CRUD Buttons */}
                <div className="flex items-center gap-1.5 pr-3 border-r border-border">
                    <button onClick={addRow} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-secondary text-white rounded-lg transition-all text-xs font-bold shadow-sm" title="Add blank row">
                        <Plus size={13} /> Row
                    </button>
                    <button onClick={duplicateLastRow} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-text-primary rounded-lg transition-all text-xs font-bold" title="Duplicate last row">
                        <Copy size={13} /> Duplicate
                    </button>
                    <button onClick={handleAiGenerate} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all text-xs font-bold shadow-sm" title="AI generate action tasks">
                        <Bot size={13} /> AI Tasks
                    </button>
                    <div className="h-5 w-px bg-border mx-0.5" />
                    <button
                        onClick={deleteSelectedRows}
                        disabled={selectedCount === 0}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all text-xs font-bold ${selectedCount > 0 ? 'bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 border border-red-200 dark:border-red-800' : 'bg-slate-50 dark:bg-slate-900/50 text-slate-400 cursor-not-allowed border border-border/50'}`}
                    >
                        <Trash2 size={13} /> Delete {selectedCount > 0 && `(${selectedCount})`}
                    </button>
                </div>

                {/* Wrap Text Toggle */}
                <div className="flex items-center gap-1.5 pr-3 border-r border-border">
                    <button
                        onClick={() => setWrapText(!wrapText)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold transition-all text-xs border ${!wrapText
                            ? 'bg-blue-600 text-white border-blue-700 shadow-sm'
                            : 'bg-slate-100 dark:bg-slate-800 text-text-secondary hover:bg-slate-200 border-border'
                            }`}
                        title={wrapText ? 'Switch to truncate (no wrap)' : 'Wrap text is ON — click to truncate'}
                    >
                        {wrapText ? <><WrapText size={13} /> Wrapping</> : <><AlignLeft size={13} /> No Wrap</>}
                    </button>
                </div>

                {/* Search */}
                <div className="flex-1 min-w-[240px] relative group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary group-focus-within:text-primary transition-colors" size={15} />
                    <input
                        type="text"
                        placeholder="Search FMEA rows..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-9 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />
                    {searchTerm && (
                        <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary">
                            <RotateCcw size={13} />
                        </button>
                    )}
                </div>

                {/* Right Controls */}
                <div className="flex items-center gap-1.5 pl-2 border-l border-border">
                    <button onClick={resetFilters} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-text-secondary transition-colors" title="Clear all filters">
                        <RotateCcw size={16} />
                    </button>
                    {/* ⚙️ Layout Settings Gear */}
                    <button
                        onClick={onOpenLayoutSettings}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-text-secondary hover:text-primary rounded-lg transition-all text-xs font-bold border border-border"
                        title="Layout & Display Settings"
                    >
                        <Settings2 size={15} />
                        Layout
                    </button>
                </div>
            </div>
        </div>
    );
};
